import { PartType } from '@models/editor/Part'
import { InstrumentManifestType } from '@utils/nf-webaudio/instrument-manifest'
import { ScoreMix } from '@utils/nf-webaudio/sequencer.types'

import nfsyn from './nf-webaudio'

enum ReverbState {
  SETUP_WITHOUT_REVERB = -1,
  NO_SETUP = 0,
  SETUP_FOR_REVERB = 1,
}

// TODO: fill this out more
export enum ReverbType {
  MEDIUM = 'Medium',
  Large = 'Large',
}

export interface SynthContextOptions {
  sampleRate?: number
  preview?: boolean
  manifestLocation: string
  instrumentBase: string //base URI for manifest and instrument loading
  basicPerformance?: boolean //use (or not) reverb
  offline: boolean //creates an OfflineAudioContext instead of a regular AudioContext
  totalTime?: number //totalTime for OfflineAudioContext
  premium: boolean //true|false -- premium instruments flag. It can be overwritten later if needed.
}

// Typescript why do you do this??
interface progressCallbacks {
  (loadsStarted: number, loadsFinished: number): void
}

interface fn {
  (): void
}
export interface SynthesisContextType {
  init: () => SynthesisContextType
  setSynthesizerForPart: (part: PartType, callback: (value: unknown) => void) => void
  incLoading: () => void
  decLoading: () => void
  setMasterGain: (level: number) => void
  applyReverbImpulse: (opt_callback?: () => void) => void
  setReverbLevel: (reverbLevel: number) => void
  options: SynthContextOptions
  active: boolean
  paused: boolean
  pausedTime: number
  loadsStarted: number
  loadsFinished: number
  loadedCallbacks: fn[]
  loadingProgressCallbacks: progressCallbacks[]
  audioContextResumed: boolean
  tooManyContexts: boolean
  lowQuality: boolean
  reverbState: ReverbState
  bufferedReverbType: ReverbType | null
  latencyOffset: number
  context: OfflineAudioContext | AudioContext
  compressor: DynamicsCompressorNode
  supportsLooping: boolean
  supportsStart: boolean
  reverb: ConvolverNode
  wetGain: GainNode
  masterGain: GainNode
  dryGain: GainNode
  metronomeGain: GainNode
  destination: GainNode
  manifest: InstrumentManifestType
  scoreMix: ScoreMix | null
  premium: boolean
}

// error message from the browser
const TOO_MANY_CONTEXTS_ERROR =
  "Failed to construct 'AudioContext': The number of hardware contexts provided (6) is greater than or equal to the maximum bound (6)."
const SynthesisContext = function (this: SynthesisContextType, options: SynthContextOptions): void {
  this.options = options
}

// Construct a contextual object for all synthesis
SynthesisContext.prototype.init = async function (this: SynthesisContextType) {
  this.active = false

  // Handle pause. The first, is just a flag. The second is the position where it's been paused (or where it will resume).
  this.paused = false
  this.pausedTime = 0

  this.manifest = new nfsyn.InstrumentManifest(this, this.options)

  this.loadsStarted = 0
  this.loadsFinished = 0
  this.loadedCallbacks = []
  this.loadingProgressCallbacks = []
  this.audioContextResumed = false
  this.tooManyContexts = false
  this.lowQuality = this.options.sampleRate ? this.options.sampleRate < 44100 : false

  // This variable denotes how the audio processor graph is set up for reverb.
  this.reverbState = ReverbState.NO_SETUP

  // This variable records the type of the reverb impulse response currently buffered in
  // the reverb convolver.
  this.bufferedReverbType = null

  // BLATANT HACK to correct for latency on Android platform; measured only on a prototype Nexus 7
  this.latencyOffset = navigator.userAgent.match(/Android/) ? 0.25 : 0

  try {
    // Create an OfflineAudioContext or a regular AudioContext
    if (this.options.offline) {
      const sampleRate = this.options.sampleRate
        ? this.options.sampleRate
        : nfsyn.EXPORT_AUDIO_SAMPLE_RATE
      const totalTime = this.options.totalTime || 0
      const length =
        (totalTime + nfsyn.WARM_UP_DELAY + nfsyn.EXPORT_AUDIO_DECAY_EXTRA_TIME) * sampleRate
      const numChannels = 2
      const contextKlass = OfflineAudioContext
      this.context = new contextKlass(numChannels, length, sampleRate)
    } else {
      const ContextClass = window.AudioContext
      if (!ContextClass) {
        console.info('Falling back to deprecated webkitAudioContext')
        // ContextClass = window.webkitAudioContext;
      }
      this.context = new ContextClass()
      if (this.context === null) {
        // In Safari (and iOS browsers), sometimes the webkitAudioContext silently fails to be instantiated and is returned as null.
        // If this is the case, we bail so that we display a message about there being too many audio contexts.
        return
      }
    }
  } catch (e) {
    console.error(`nfsyn.SynthesisContext ERROR!`)
    console.log(e)
    if (e instanceof DOMException && e.message == TOO_MANY_CONTEXTS_ERROR) {
      // this is on a page with more embeds than the browser can handle
      this.tooManyContexts = true
    } else {
      // what is going on here?
    }
    return
  }

  await this.manifest.promise()
  this.active = true

  this.compressor = this.context.createDynamicsCompressor()
  this.compressor.threshold.value = -24
  this.compressor.ratio.value = 12
  this.compressor.knee.value = 30
  this.compressor.attack.value = 0.003
  this.compressor.release.value = 0.25
  this.compressor.connect(this.context.destination)

  const bufferSource = this.context.createBufferSource()
  if (bufferSource.loopStart === undefined) {
    console.info('This AudioContext does not support looping.')
  } else {
    this.supportsLooping = true
  }

  // alias old alternate names to new names
  const bufferSourceProto = bufferSource.constructor.prototype
  if (!bufferSourceProto.start) {
    console.info('patching start/stop')
    bufferSourceProto.start = bufferSourceProto.noteOn
    bufferSourceProto.stop = bufferSourceProto.noteOff
  } else {
    this.supportsStart = true
  }

  const contextProto = this.context.constructor.prototype
  if (!contextProto.createGain) {
    console.info('patching createGain')
    contextProto.createGain = contextProto.createGainNode
  }

  const gainNode = this.context.createGain()
  const audioParamProto = gainNode.gain.constructor.prototype
  if (!audioParamProto.setTargetAtTime) {
    console.info('patching setTargetAtTime')
    audioParamProto.setTargetAtTime = audioParamProto.setTargetValueAtTime
  }

  // Create the right hand side webaudio nodes.  They are wired up by wireForReverb and wireForNoReverb below.
  this.reverb = this.context.createConvolver() // (2) above
  this.wetGain = this.context.createGain() // (3)
  this.wetGain.gain.value = 0
  this.masterGain = this.context.createGain() // (1)
  this.dryGain = this.context.createGain() // (4)
  this.metronomeGain = this.context.createGain() // (5)
  this.destination = this.masterGain

  // this.score = null;
  this.scoreMix = null

  return this
}

// Given a notation.model.Part, create a synthesizer for it if it doesn't already have one. Saves
// it in the Part's synthesizer field.
SynthesisContext.prototype.setSynthesizerForPart = function (
  this: SynthesisContextType,
  part: PartType,
  callback: () => void
): void {
  if (!part.synthesizer) {
    // wraps the XML of an instrument in the InstrumentManifest and provides getters to inspect
    const descriptor = this.manifest.getInstrumentByUri(true, part.instrumentUri)

    if (descriptor) {
      // Creates an instance of SampleInstrument
      // Sampled instrument representation and synthesis. An instrument can produce both pre-scheduled
      // notes (call scheduleNote) and real-time notes (call noteOn and noteOff).
      const synthesizer = descriptor.createInstrument(part)
      part.synthesizer = synthesizer

      this.incLoading()
      // Each instrument has a specification.xml file - it's a data structure that holds which wav files correspond to each pitch
      // Here we're grabbing it from the server

      // After the specification.xml is loaded up each it loads up all of the audio files (notes) to play the sequencer
      synthesizer.load(() => {
        this.decLoading()
        if (callback) {
          callback()
        }
      })
    } else {
      console.error('Error loading instrument')
      // part.dispatchErrorLoadingInstrument(part.instrumentUri)
    }
  }
}

// // private - Increments the counter that keeps track of how many asynchronous loading operations
// // have been started.
SynthesisContext.prototype.incLoading = function (this: SynthesisContextType) {
  this.loadsStarted++
}

// // private - Increments the counter that keeps track of how many asynchronous loading operations
// // have finished. Calls any progress callbacks registered by whenLoaded() below. If all resources
// // are done loading, calls any completion callbacks registered with whenLoaded(), resets the
// // counters to zero, and unregisters all callbacks.
SynthesisContext.prototype.decLoading = function (this: SynthesisContextType) {
  this.loadsFinished++
  for (let i = 0; i < this.loadingProgressCallbacks.length; i++) {
    this.loadingProgressCallbacks[i](this.loadsFinished, this.loadsStarted)
  }
  if (this.loadsFinished >= this.loadsStarted) {
    this.loadsStarted = this.loadsFinished = 0
    this.loadingProgressCallbacks = []
    for (let i = 0; i < this.loadedCallbacks.length; i++) {
      this.loadedCallbacks[i]()
    }
    this.loadedCallbacks = []
  }
}

// // Set up the right hand side of the audio processing graph without reverb.
// //
// //               +----------+   +-------+
// // instruments-->| Gain (1) |-->| Compr |---> dest
// //               +----------+   +-------+
// //
SynthesisContext.prototype.wireForNoReverb = function (this: SynthesisContextType): void {
  if (this.reverbState != ReverbState.SETUP_WITHOUT_REVERB) {
    this.masterGain.disconnect()
    this.masterGain.connect(this.compressor)
    this.metronomeGain.connect(this.compressor)
    this.reverbState = -1
  }
}

// // Loads a reverb impulse response for the given reverb type, then calls opt_callback (if
// // provided). If reverbType is null, disable reverb.  If the URL is the same one that's
// // already in use, it doesn't reload it (but still call opt_callback).
SynthesisContext.prototype.applyReverbImpulse = function (opt_callback?: () => void) {
  const callback = opt_callback

  this.wireForNoReverb()
  if (callback) {
    callback()
  }
}

SynthesisContext.prototype.setMasterGain = function (level) {
  if (this.masterGain) {
    this.masterGain.gain.value = nfsyn.utils.decibelsToFactor(level)
    this.metronomeGain.gain.value = nfsyn.utils.decibelsToFactor(level)
  }
}

SynthesisContext.prototype.setReverbLevel = function (level) {
  const wetGain = nfsyn.utils.decibelsToFactor(level)
  this.wetGain.gain.value = wetGain
  // Total power from dryGain and wetGain should remain constant.
  this.dryGain.gain.value = Math.sqrt(1 - wetGain * wetGain)
}

export default SynthesisContext
